import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Card } from 'react-bootstrap';
import CustomModal from '../Shared/CustomModal/CustomModal';
import profileService from '../../services/profileService';
import authService from '../../services/authService';
import showToast from '../../utils/toast';
import { Images } from '../../assets/assets';
import './Profile.css';

export default function Profile() {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Profile form data
  const [profileData, setProfileData] = useState({
    username: '',
  });

  // Password reset data
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Profile image
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileService.getProfile();
      if (response.success && response.data) {
        const profile = response.data;
        setProfile(profile);
        setProfileData({
          username: profile.username || '',
        });
        
        // Set profile image preview
        if (profile.profilePicture) {
          const imageUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/uploads/${profile.profilePicture}`;
          setProfileImagePreview(imageUrl);
        } else {
          setProfileImagePreview(null);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showToast.error(error.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        showToast.error('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast.error('Image size must be less than 5MB');
        return;
      }

      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!profileImage) {
      showToast.error('Please select an image to upload');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await profileService.uploadProfileImage(profileImage);
      if (response.success) {
        showToast.success('Profile image uploaded successfully');
        setProfileImage(null);
        await fetchProfile(); // Refresh profile
        // Dispatch event to update header profile picture
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } else {
        showToast.error(response.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast.error(error.message || 'Failed to upload profile image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await profileService.updateProfile(profileData);
      if (response.success) {
        showToast.success('Profile updated successfully');
        // Update profile state and form data
        setProfile(response.data);
        setProfileData({
          username: response.data.username || '',
        });
      } else {
        showToast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showToast.error('Please fill all fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showToast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      const response = await profileService.resetPassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      if (response.success) {
        showToast.success('Password reset successfully');
        setShowResetPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        showToast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      showToast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="profile-container">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container profile">
      <div className="custom-card">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h2>My Profile</h2>
            <p>Manage your profile information and settings</p>
          </div>
        </div>
        <div className="custom-card-body">
          {/* Profile Image Section */}
          <div className="profile-image-section">
            <div className="profile-image-wrapper">
              {profileImagePreview ? (
                <img 
                  src={profileImagePreview} 
                  alt="Profile" 
                  className="profile-image"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <img 
                    src={Images.UserProfile} 
                    alt="Profile" 
                    className="profile-image"
                  />
                </div>
              )}
              <div className="profile-image-overlay">
                <label htmlFor="profileImageInput" className="profile-image-upload-btn">
                  <span className="icon">📷</span>
                  Change Photo
                </label>
                <input
                  id="profileImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            {profileImage && (
              <div className="mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUploadImage}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading...
                    </>
                  ) : (
                    'Upload Image'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    setProfileImage(null);
                    setProfileImagePreview(profile?.profilePicture 
                      ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/uploads/${profile.profilePicture}`
                      : null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        

          {/* Profile Form */}
          <Form onSubmit={handleUpdateProfile}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={profileData.username}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    Email cannot be changed
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            <div className="profile-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Profile'
                )}
              </Button>
              <Button
                type="button"
                variant="outline-danger"
                onClick={() => setShowResetPasswordModal(true)}
                disabled={loading}
              >
                Reset Password
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Reset Password Modal */}
      <CustomModal
        show={showResetPasswordModal}
        onHide={() => {
          setShowResetPasswordModal(false);
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        }}
        title="Reset Password"
        footerButtons={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => {
              setShowResetPasswordModal(false);
              setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              });
            },
            disabled: loading
          },
          {
            label: 'Reset Password',
            variant: 'danger',
            onClick: handleResetPassword,
            disabled: loading,
            loading: loading
          }
        ]}
      >
        <Form onSubmit={(e) => {
          e.preventDefault();
          handleResetPassword(e);
        }}>
          <Form.Group className="mb-3">
            <Form.Label>Current Password *</Form.Label>
            <Form.Control
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              required
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>New Password *</Form.Label>
            <Form.Control
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              required
              disabled={loading}
              minLength={6}
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters long
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm New Password *</Form.Label>
            <Form.Control
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              required
              disabled={loading}
              minLength={6}
            />
          </Form.Group>
        </Form>
      </CustomModal>
    </div>
  );
}

